import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Users,
  Clipboard,
  Briefcase,
  UserCheck,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Package,
  ClipboardCheck,
  FileText,
  FilePen,
  FolderOpen,
  Inbox,
  CircleCheckBig,
  UserRoundPlus,
  Cog,
  Wrench,
  ArrowLeftRight,
  Undo2,
  TriangleAlert,
  Hammer,
} from "lucide-react";
import type { UserProfile } from '@/services/api';
import logoSmall from '@/assets/logo.svg';
import logoBig from '@/assets/big-logo.svg';
import circleDollarIcon from '@/assets/circle-dollar.svg';
import ModuleSelector from '@/features/configuracion/components/ModuleSelector';
import { hasAnyRole } from '@/shared/lib/roleUtils';
import { config } from '@/config/env';
import { ALMACEN, RH, PROVEEDORES, GENERALES } from '@/constants/permissions';
import { entradasDeReclutamiento } from '@/features/rh/lib/menuDeReclutamiento';

const CustomCircleDollarIcon = ({ className }: { className?: string }) => (
  <img src={circleDollarIcon} alt="Pagos" className={className} />
);

interface SubMenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  route: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  route?: string;
  adminOnly?: boolean;
  masterOnly?: boolean;
  subItems?: SubMenuItem[];
}
interface SidebarProps {
  activeRoute?: string;
  onNavigate?: (route: string) => void;
  user?: UserProfile | null;
  onCollapsedChange?: (collapsed: boolean) => void;
}
/** Ícono de cada entrada del grupo Reclutamiento; la lista y el orden viven en la lib. */
const ICONOS_DE_RECLUTAMIENTO: Record<string, React.ElementType> = {
  "panel-candidatos": Users,
  "entrevistas-rh": UserCheck,
  "entrevistas-solicitante": CircleCheckBig,
  "cierre-seleccion": CircleCheckBig,
  "validacion-direccion": CircleCheckBig,
};

const Sidebar = ({
  activeRoute = "dashboard",
  onNavigate,
  user,
  onCollapsedChange,
}: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isPermanentExpanded, setIsPermanentExpanded] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState<string[]>([]);
  const [currentModuleId, setCurrentModuleId] = useState<string | null>(
    user?.currentModuleId || null,
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [_showModuleSelector, setShowModuleSelector] = useState(false);
  const [moduleKeyById, setModuleKeyById] = useState<Record<string, string>>(
    {},
  );

  const menuItemsRH = useMemo<MenuItem[]>(() => {
    const perms: string[] = user?.rbac?.effectivePermissions ?? [];
    const has = (p: string) => perms.some((x) => x.includes(p));

    const items: MenuItem[] = [];

    items.push({
      id: "inicio",
      label: "Inicio",
      icon: Home,
      route: "dashboard",
    });

    if (has(RH.SOLICITUDES_PERSONAL_READ)) {
      items.push({
        id: "solicitudes-personal",
        label: "Solicitudes de personal",
        icon: Clipboard,
        route: "",
        subItems: [
          {
            id: "mis-solicitudes",
            label: "Mis solicitudes",
            icon: FilePen,
            route: "job-requests",
          },
        ],
      });
    }

    if (has(RH.GESTION_SOLICITUDES_READ)) {
      items.push({
        id: "solicitudes",
        label: "Gestión de solicitudes",
        icon: FileText,
        route: "",
        subItems: [
          {
            id: "solicitudes",
            label: "Bandeja de solicitudes",
            icon: Inbox,
            route: "job-requests-rh",
          },
        ],
      });
    }

    // Un solo grupo, en el orden del proceso (SRH-310). Cada entrada sale por
    // su propio permiso: el grupo aparece si hay al menos una.
    const entradasReclutamiento = entradasDeReclutamiento(has).map((entrada) => ({
      ...entrada,
      icon: ICONOS_DE_RECLUTAMIENTO[entrada.id] ?? Users,
    }));
    if (entradasReclutamiento.length > 0) {
      items.push({
        id: "reclutamiento",
        label: "Reclutamiento",
        icon: Briefcase,
        route: "",
        subItems: entradasReclutamiento,
      });
    }

    if (
      has(RH.EMPLEADOS_EXPEDIENTES_READ) &&
      has(RH.EMPLEADOS_PREREGISTRO_READ)
    ) {
      items.push({
        id: "empleados",
        label: "Empleados",
        icon: UserRoundPlus,
        route: "",
        adminOnly: false,
        subItems: [
          {
            id: "contratacion",
            label: "Preregistro",
            icon: FilePen,
            route: "hiring/candidates",
          },
          {
            id: "empleados",
            label: "Expedientes",
            icon: FolderOpen,
            route: "employees",
          },
        ],
      });
    }

    if (
      has(RH.EMPLEADOS_PREREGISTRO_READ) &&
      !has(RH.EMPLEADOS_EXPEDIENTES_READ)
    ) {
      items.push({
        id: "empleados",
        label: "Empleados",
        icon: UserRoundPlus,
        route: "",
        adminOnly: false,
        subItems: [
          {
            id: "contratacion",
            label: "Preregistro",
            icon: FilePen,
            route: "hiring/candidates",
          },
        ],
      });
    }

    if (
      has(RH.EMPLEADOS_EXPEDIENTES_READ) &&
      !has(RH.EMPLEADOS_PREREGISTRO_READ)
    ) {
      items.push({
        id: "empleados",
        label: "Empleados",
        icon: UserRoundPlus,
        route: "",
        adminOnly: false,
        subItems: [
          {
            id: "empleados",
            label: "Expedientes",
            icon: FolderOpen,
            route: "employees",
          },
        ],
      });
    }

    if (
      has(GENERALES.CATALOGOS_PROYECTOS_READ) ||
      has(GENERALES.CATALOGOS_ROLES_READ) ||
      has(GENERALES.CATALOGOS_AREAS_READ) ||
      has(GENERALES.CATALOGOS_PUESTOS_READ) ||
      has(PROVEEDORES.PROVEEDORES_CONSULTAR)
    ) {
      items.push({
        id: "admin",
        label: "Configuración",
        icon: Cog,
        route: "admin",
        adminOnly: false,
      });
    }

    return items;
  }, [user?.rbac?.effectivePermissions]);

  // Menú para módulo de Proveedores (ID: d035c8bd-1fad-49b0-9837-b087e11c1101)
  const menuItemsProveedores = useMemo<MenuItem[]>(() => {
    const perms: string[] = user?.rbac?.effectivePermissions ?? [];
    const has = (p: string) => perms.some((x) => x.includes(p));

    const items: MenuItem[] = [];

    if (has(PROVEEDORES.DASHBOARD_CONSULTAR)) {
      items.push({
        id: "inicio",
        label: "Inicio",
        icon: Home,
        route: "dashboard",
      });
    }

    if (has(PROVEEDORES.PROVEEDORES_CONSULTAR)) {
      items.push({
        id: "proveedores",
        label: "Proveedores",
        icon: Users,
        route: "super-admin-proveedores",
      });
    }

    if (has(PROVEEDORES.PROVEEDORES_CONSULTAR)) {
      items.push({
        id: "solicitudes",
        label: "Solicitudes",
        icon: Briefcase,
        route: "solicitudes-proveedores",
      });
    }

    if (has(PROVEEDORES.ORDENES_COMPRA_CONSULTAR)) {
      items.push({
        id: "ordenes-compra",
        label: "Órdenes de compra",
        icon: Package,
        route: "rh/purchase-orders",
      });

      items.push({
        id: "pagos",
        label: "Pagos",
        icon: CustomCircleDollarIcon, // Usamos el SVG custom
        route: "pagos",
      });
    }

    if (has(PROVEEDORES.VALIDACIONES_CONSULTAR)) {
      items.push({
        id: "evaluaciones",
        label: "Evaluaciones",
        icon: ClipboardCheck,
        route: "rh/evaluations",
      });
    }

    const canSeeConfig =
      has(GENERALES.CATALOGOS_PROYECTOS_READ) ||
      has(GENERALES.CATALOGOS_ROLES_READ) ||
      has(GENERALES.CATALOGOS_AREAS_READ) ||
      has(GENERALES.CATALOGOS_PUESTOS_READ) ||
      has(PROVEEDORES.PROVEEDORES_CONSULTAR);

    const isProveedorAdminRole = hasAnyRole(user, ["MASTER"]) ?? false;

    if (canSeeConfig || isProveedorAdminRole) {
      items.push({
        id: "admin",
        label: "Configuración",
        icon: Settings,
        route: "admin",
        adminOnly: false,
      });
    }

    return items;
  }, [user?.rbac?.effectivePermissions, user?.roles]);

  // Menú para módulo de Almacén
  const menuItemsAlmacen = useMemo<MenuItem[]>(() => {
    const perms: string[] = user?.rbac?.effectivePermissions ?? [];
    const isMaster = user?.rbac?.isMaster ?? false;
    const has = (p: string) => isMaster || perms.some((x) => x.includes(p));

    const items: MenuItem[] = [];

    // Herramientas, Asignación y Devolución listan herramientas, y el back pide
    // "ver" (todas) o "ver_asignadas" para ese listado. Sin ninguna de las dos
    // la pantalla responde con acceso denegado, así que tampoco va en el menú.
    const verHerramientas =
      has(ALMACEN.HERRAMIENTAS_VER) || has(ALMACEN.HERRAMIENTAS_VER_ASIGNADAS);

    if (has(ALMACEN._PREFIX)) {
      items.push({
        id: "inicio",
        label: "Inicio",
        icon: Home,
        route: "dashboard",
      });
    }
    if (verHerramientas) {
      items.push({
        id: "registro",
        label: "Herramientas",
        icon: Wrench,
        route: "almacen/registro",
      });
    }
    if (has(ALMACEN._PREFIX_MOVIMIENTOS) && verHerramientas) {
      items.push({
        id: "asignacion",
        label: "Asignación",
        icon: ArrowLeftRight,
        route: "almacen/asignacion",
      });
      items.push({
        id: "devolucion",
        label: "Devolución",
        icon: Undo2,
        route: "almacen/devolucion",
      });
    }
    if (has(ALMACEN.REPORTES_VER)) {
      items.push({
        id: "reportes",
        label: "Incidencias",
        icon: TriangleAlert,
        route: "almacen/reportes",
      });
    }
    if (has(ALMACEN._PREFIX_MANT)) {
      items.push({
        id: "mantenimiento",
        label: "Mantenimiento",
        icon: Hammer,
        route: "almacen/mantenimiento",
      });
    }
    if (has(ALMACEN.HISTORIAL_VER)) {
      items.push({
        id: "historial",
        label: "Historial",
        icon: FileText,
        subItems: [
          { id: "historial-herramientas", label: "Por herramienta", icon: Wrench,  route: "almacen/historial/herramientas" },
          { id: "historial-responsable",  label: "Por responsable",  icon: Users,   route: "almacen/historial/responsable"  },
        ],
      });
    }

    // Configuración: visible para super-admin o quien tenga permisos de catálogos
    const canSeeConfig =
      isMaster ||
      has(GENERALES.CATALOGOS_PROYECTOS_READ) ||
      has(GENERALES.CATALOGOS_ROLES_READ) ||
      has(GENERALES.CATALOGOS_AREAS_READ) ||
      has(GENERALES.CATALOGOS_PUESTOS_READ);

    if (canSeeConfig) {
      items.push({
        id: "config-almacen",
        label: "Configuración",
        icon: Cog,
        route: "admin",
      });
    }

    return items;
  }, [user?.rbac?.effectivePermissions, user?.rbac?.isMaster, user?.roles]);

  useEffect(() => {
    const loadModuleKeys = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      try {
        const response = await fetch(config.apiBaseUrl + "/users/me/modules", {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) return;

        const data = await response.json();
        const mapping: Record<string, string> = {};

        if (Array.isArray(data?.modules)) {
          data.modules.forEach((module: { id?: string; key?: string }) => {
            if (module?.id && module?.key) {
              mapping[module.id] = module.key;
            }
          });
        }

        setModuleKeyById(mapping);
      } catch (error) {
        console.error("Error loading module keys", error);
      }
    };

    loadModuleKeys();
  }, [user?.id, user?.currentModuleId]);

  const getMenuForModule = (
    moduleKey?: string,
    _moduleId?: string,
  ): MenuItem[] => {
    if (moduleKey === "mod_proveedores") return menuItemsProveedores;
    if (moduleKey === "mod_almacen" || moduleKey === "almacen") return menuItemsAlmacen;
    if (moduleKey === "rh") return menuItemsRH;

    // Fallback 1: detectar por URL (rutas de almacén)
    if (location.pathname.startsWith("/almacen/")) return menuItemsAlmacen;

    // Fallback 2: detectar por permisos efectivos cuando el módulo no está mapeado
    const perms: string[] = user?.rbac?.effectivePermissions ?? [];
    const hasAlmacen    = perms.some((p) => p.startsWith("almacen."));
    const hasRH         = perms.some((p) => p.startsWith("rh."));
    const hasProveedores = perms.some((p) => p.startsWith("proveedores."));

    if (hasAlmacen && !hasRH && !hasProveedores) return menuItemsAlmacen;
    if (hasProveedores && !hasRH)                return menuItemsProveedores;

    // Sin permisos de ningun modulo: solo configuracion y catalogos, que son
    // transversales. Antes caia en el menu de RH y le mostraba Inicio, que
    // lleva al dashboard de RH — datos que este usuario no puede ver.
    if (!hasAlmacen && !hasRH && !hasProveedores) {
      return menuItemsRH.filter((item) => item.id === "admin");
    }

    return menuItemsRH;
  };

  const getDefaultRouteForModule = (_moduleKey?: string) => {
    return "/dashboard";
  };

  // Escuchar cambios de modulo
  useEffect(() => {
    const handleModuleChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { moduleId } = customEvent.detail;
      const moduleKey = moduleKeyById[moduleId];
      const targetRoute = getDefaultRouteForModule(moduleKey);

      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentModuleId(moduleId);
        setOpenSubMenus([]);
        navigate(targetRoute);

        setTimeout(() => {
          setIsTransitioning(false);
        }, 150);
      }, 150);
    };

    window.addEventListener("module:changed", handleModuleChanged);
    return () =>
      window.removeEventListener("module:changed", handleModuleChanged);
  }, [moduleKeyById, navigate]);

  useEffect(() => {
    if (user?.currentModuleId !== currentModuleId) {
      setCurrentModuleId(user?.currentModuleId || null);
    }
  }, [user?.currentModuleId, currentModuleId]);

  const handleNavigation = (route: string) => {
    if (onNavigate) {
      onNavigate(route);
    } else {
      navigate(`/${route}`);
    }
  };

  const toggleSubMenu = (menuId: string) => {
    setOpenSubMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId],
    );
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isPermanentExpanded;
    setIsPermanentExpanded(newCollapsedState);
    setIsCollapsed(!newCollapsedState);
    if (onCollapsedChange) {
      onCollapsedChange(!newCollapsedState);
    }
  };

  const handleMouseEnter = () => {
    if (!isPermanentExpanded) {
      setIsCollapsed(false);
    }
  };

  const handleMouseLeave = () => {
    if (!isPermanentExpanded) {
      setIsCollapsed(true);
    }
  };

  // Determine current route from location if activeRoute is not provided
  const getCurrentRoute = () => {
    if (activeRoute) return activeRoute;

    const pathSegment = location.pathname.split("/")[1] || "dashboard";

    if (pathSegment === "admin") return "admin";
    if (location.pathname.startsWith("/hiring/candidates"))
      return "hiring/candidates";
    if (pathSegment === "hiring") return "contratacion";
    if (pathSegment === "employees") return "empleados";

    if (location.pathname.includes("/pagos")) {
      return "pagos";
    }

    if (location.pathname.startsWith("/rh/purchase-orders")) {
      return "ordenes-compra";
    }
    if (location.pathname.startsWith("/rh/evaluations")) {
      return "evaluaciones";
    }
    if (location.pathname === "/hiring-manager-interviews") {
      return "hiring-manager-interviews";
    }
    if (location.pathname === "/job-requests-rh") {
      return "job-requests-rh";
    }
    if (location.pathname === "/job-requests") {
      return "job-requests";
    }

    return pathSegment;
  };

  const currentRoute = getCurrentRoute();

  const activeModuleId = user?.currentModuleId || currentModuleId;
  const activeModuleKey = activeModuleId
    ? moduleKeyById[activeModuleId]
    : undefined;
  const menuToUse = getMenuForModule(
    activeModuleKey,
    activeModuleId || undefined,
  );

  return (
    <aside
      className={`fixed left-0 top-0 h-screen border-r border-gray-200 transition-all duration-300 ease-in-out z-40 flex flex-col ${
        isCollapsed ? "w-20" : "w-80 shadow-xl"
      }`}
      style={{
        backgroundColor: "#FFFFFF",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo Section */}
      <div
        className="h-16 flex items-center justify-center px-4 border-b border-gray-200 flex-shrink-0"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        {!isCollapsed ? (
          <img
            src={logoBig}
            alt="SLYRSA"
            className="h-12 w-auto"
            style={{ filter: "none" }}
          />
        ) : (
          <img
            src={logoSmall}
            alt="SLYRSA"
            className="h-10 w-10"
            style={{ filter: "none" }}
          />
        )}
      </div>

      {/* Collapse Button */}
      <button
        onClick={toggleCollapse}
        className="absolute -right-3 top-20 border border-gray-300 rounded-full p-1.5 shadow-md hover:shadow-lg transition-all duration-200 z-50"
        style={{ backgroundColor: "#FFFFFF", color: "#4B5563" }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#F9FAFB")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "#FFFFFF")
        }
        aria-label={isCollapsed ? "Expandir sidebar" : "Contraer sidebar"}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        )}
      </button>

      {/* Navigation Menu */}
      <nav
        className="flex-1 py-6 px-3 overflow-y-auto"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <style>{`
          nav::-webkit-scrollbar {
            display: none;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeOut {
            from { opacity: 1; transform: translateY(0); }
            to { opacity: 0; transform: translateY(-10px); }
          }
          .menu-transition-enter {
            animation: fadeIn 0.3s ease-out forwards;
          }
          .menu-transition-exit {
            animation: fadeOut 0.15s ease-in forwards;
          }
        `}</style>
        <ul
          key={activeModuleId || "default"}
          className={`space-y-1 ${
            isTransitioning ? "menu-transition-exit" : "menu-transition-enter"
          }`}
        >
          {menuToUse.map((item) => {
            const Icon = item.icon;
            const isActive = item.route ? currentRoute === item.route : false;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isSubMenuOpen = openSubMenus.includes(item.id);

            return (
              <li key={item.id}>
                <button
                  onClick={() => {
                    if (hasSubItems) {
                      toggleSubMenu(item.id);
                      if (item.route) {
                        handleNavigation(item.route);
                      }
                    } else if (item.route) {
                      handleNavigation(item.route);
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative ${
                    isActive
                      ? "bg-[#C3CBEB] text-[#232F61] font-semibold"
                      : "text-[#404040] hover:bg-[#C3CBEB] hover:text-[#232F61]"
                  } ${isCollapsed ? "justify-center" : ""}`}
                  style={{
                    border: "none",
                    padding: "0.625rem 0.75rem",
                    textTransform: "none",
                    letterSpacing: "normal",
                  }}
                >
                  <Icon
                    className={`flex-shrink-0 w-5 h-5 transition-colors ${
                      isActive
                        ? "text-[#404040]" // Mantiene su color si está activo
                        : // Agregamos un filtro sutil si necesitas que tu SVG tome el color (CSS) o lo dejamos original
                          "text-[#232F61] group-hover:text-[#404040]"
                    }`}
                  />
                  {!isCollapsed && (
                    <>
                      <span className="text-sm font-medium truncate flex-1 text-left">
                        {item.label}
                      </span>
                      {hasSubItems &&
                        (isSubMenuOpen ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ))}
                    </>
                  )}

                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-6 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] pointer-events-none">
                      {item.label}
                      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                    </div>
                  )}
                </button>

                {/* Submenú */}
                {hasSubItems && isSubMenuOpen && !isCollapsed && (
                  <ul className="mt-1 ml-8 space-y-1">
                    {item.subItems!.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive = currentRoute === subItem.route;

                      return (
                        <li key={subItem.id}>
                          <button
                            onClick={() => handleNavigation(subItem.route)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 group relative text-sm ${
                              isSubActive
                                ? "bg-[#C3CBEB] text-[#232F61] font-semibold"
                                : "text-[#404040]"
                            }`}
                          >
                            <SubIcon
                              className={`flex-shrink-0 w-4 h-4 transition-colors ${
                                isSubActive
                                  ? "text-[#404040]"
                                  : "text-[#404040] "
                              }`}
                            />
                            <span className="font-medium truncate">
                              {subItem.label}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Module Selector */}
      {(() => {
        if (!isCollapsed && user) {
          return (
            <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0">
              <ModuleSelector
                user={user}
                onModuleChange={(moduleId) => {
                  const moduleKey = moduleKeyById[moduleId];
                  navigate(getDefaultRouteForModule(moduleKey));
                }}
                setShowModuleSelector={setShowModuleSelector}
              />
            </div>
          );
        } else {
          return null;
        }
      })()}

      {/* Footer */}
      {!isCollapsed && (
        <div
          className="px-4 py-3 border-t border-gray-200 flex-shrink-0"
          style={{ backgroundColor: "#FFFFFF" }}
        >
          <p className="text-xs text-gray-500 text-center font-medium">
            © {new Date().getFullYear()} SLYRSA
          </p>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
